import { useEffect, useMemo, useRef, useState } from 'react';
import { useHistory } from 'react-router-dom';
import {
    Alert,
    Box,
    Button,
    Checkbox,
    Chip,
    CircularProgress,
    Container,
    Divider,
    FormControl,
    FormControlLabel,
    InputLabel,
    LinearProgress,
    MenuItem,
    Paper,
    Select,
    Snackbar,
    Stack,
    Step,
    StepLabel,
    Stepper,
    TextField,
    Typography,
} from '@mui/material';
import {
    AddRounded,
    AutoAwesomeRounded,
    CheckCircleRounded,
    GroupsRounded,
    PersonAddAltRounded,
    SaveRounded,
    SendRounded,
    TaskAltRounded,
} from '@mui/icons-material';
import OnboardingClient, {
    CollaboratorRole,
    OnboardingStep,
    OnboardingTalentDraft,
    OnboardingWorkspace,
} from '../apiClients/OnboardingClient';
import AiTemplateClient, {
    AiChatMessage,
    AiContextualAiAnswer,
    AiTemplateCoverageConfirmation,
    AiTemplatePreview,
    AiTemplateSuggestionDecision,
} from '../apiClients/AiTemplateClient';
import ManageOrganizationClient, { BillingSummary } from '../apiClients/ManageOrganizationClient';
import { ProcessType } from '../models/dtos';
import useViewportMode from '../hooks/useViewportMode';
import { useAccessInfo } from '../contexts/AccessTokenProvider';
import ContextualAiDialog from '../components/ai/ContextualAiDialog';
import ContextualAiWorkspace from '../components/ai/ContextualAiWorkspace';
import { ContextualAiTurnResult, ContextualAiWorkflowAdapter } from '../components/ai/contextualAiTypes';
import { recallContextSummary, rememberContextSummary } from '../components/ai/ContextualAiContextMemory';
import { buildTemplateDiscoveryInterview, TemplateDiscoveryTopic } from '../components/ai/templateDiscovery';
import { buildContextualRequest, mapContextualResponse } from '../components/ai/contextualAiWire';

const steps: Array<{ key: OnboardingStep; label: string; short: string }> = [
    { key: 'CompanyProfile', label: 'Tell us about your company', short: 'Company' },
    { key: 'Collaborators', label: 'Invite setup collaborators', short: 'Collaborators' },
    { key: 'FirstPosition', label: 'Create your first position', short: 'Position' },
    { key: 'SuccessProfile', label: 'Define what success looks like', short: 'Success' },
    { key: 'Employees', label: 'Add Talent', short: 'Talent' },
    { key: 'Activation', label: 'Review pricing and activate', short: 'Activate' },
    { key: 'Complete', label: 'You’re ready', short: 'Ready' },
];

const createKey = () => globalThis.crypto?.randomUUID?.()
    ?? `${Date.now()}-${Math.random().toString(36).slice(2)}`;

const track = (eventName: string, step?: OnboardingStep) => {
    // The application console is forwarded to Seq. Keep this event deliberately
    // structured and free of names, emails, descriptions, tokens, or transcripts.
    console.info('__ Synergist.UI -- Onboarding funnel', { EventName: eventName, Step: step });
};

const money = (amount: number, currency: string) => new Intl.NumberFormat(
    undefined,
    { style: 'currency', currency: (currency || 'USD').toUpperCase() },
).format(amount || 0);

type TalentDraftInput = Omit<OnboardingTalentDraft, 'Id' | 'MaterializedTalentId'> & { DraftId?: string };

const OnboardingPage = () => {
    const client = useMemo(() => new OnboardingClient(), []);
    const history = useHistory();
    const viewport = useViewportMode();
    const { logout, reloadAfterOnboardingProvisioned, onboardingBootstrapError, subscriptionRequired, hasSubscriptionAccess, needsOnboarding } = useAccessInfo();
    const preOrganizationMode = useRef(needsOnboarding);
    const [workspace, setWorkspace] = useState<OnboardingWorkspace | null>(null);
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState('');
    const [announcement, setAnnouncement] = useState('');
    const headingRef = useRef<HTMLHeadingElement | null>(null);
    const errorRef = useRef<HTMLDivElement | null>(null);

    const loadWorkspace = async (resumed = false, preserveError = false) => {
        setBusy(true);
        if (!preserveError)
            setError('');
        try {
            const resumedWithBootstrapToken = preOrganizationMode.current;
            let loaded = resumedWithBootstrapToken
                ? await client.StartOrResume()
                : await client.GetWorkspace();
            // A bootstrap token may legitimately resume a workspace whose company
            // was already provisioned. Do not expose any organization-scoped step
            // until the provider has exchanged and installed the matching org token.
            if (loaded.OrganizationId) {
                const organizationId = loaded.OrganizationId;
                const switched = await reloadAfterOnboardingProvisioned(organizationId);
                if (!switched)
                    throw new Error('Organization access is not ready.');
                preOrganizationMode.current = false;
                // StartOrResume ran under the bootstrap identity. Its response can
                // already be stale by the time token exchange completes (for
                // example, a concurrent or recovered provisioning write may have
                // advanced rowversion). Reload under the organization token before
                // exposing any versioned mutation so the first save uses the exact
                // authoritative concurrency token.
                if (resumedWithBootstrapToken) {
                    loaded = await client.GetWorkspace();
                    if (loaded.OrganizationId !== organizationId)
                        throw new Error('Organization access is not ready.');
                }
            }
            setWorkspace(loaded);
            track(resumed || loaded.Status !== 'NotStarted' ? 'resumed' : 'started', loaded.CurrentStep);
        } catch (reason) {
            if (!preOrganizationMode.current) {
                // Existing-organization users may only enter this route when an
                // active onboarding workspace already exists for that org.
                history.replace('/');
                return;
            }
            setError('We could not load your setup. Check your connection and try again.');
        } finally {
            setBusy(false);
        }
    };

    useEffect(() => {
        if (!onboardingBootstrapError)
            void loadWorkspace(true);
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        if (!workspace)
            return;
        headingRef.current?.focus();
        track('step_viewed', workspace.CurrentStep);
    }, [workspace?.CurrentStep]); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        if (error)
            errorRef.current?.focus();
    }, [error]);

    const goToStep = async (step: OnboardingStep, eventName?: 'step_completed' | 'step_skipped', sourceWorkspace?: OnboardingWorkspace) => {
        const currentWorkspace = sourceWorkspace ?? workspace;
        if (!currentWorkspace)
            return;
        setBusy(true);
        setError('');
        try {
            const goingBack = !eventName;
            const saved = await client.MoveStep({
                ExpectedVersion: currentWorkspace.Version,
                IdempotencyKey: createKey(),
                Step: goingBack ? step : currentWorkspace.CurrentStep,
                Action: goingBack ? 'Back' : eventName === 'step_skipped' ? 'Skip' : 'Complete',
            });
            setWorkspace(saved);
            setAnnouncement('Progress saved');
            if (eventName)
                track(eventName, currentWorkspace.CurrentStep);
        } catch (reason) {
            if (/conflict|stale|409/i.test(`${reason}`)) {
                setError('This setup changed in another tab. We refreshed the latest version; review it before continuing.');
                await loadWorkspace(true, true);
            } else {
                setError('Your progress could not be saved. Please try again.');
            }
        } finally {
            setBusy(false);
        }
    };

    const currentIndex = Math.max(0, steps.findIndex(step => step.key === workspace?.CurrentStep));
    const currentMeta = steps[currentIndex];

    if (onboardingBootstrapError)
        return <Container maxWidth="sm" sx={{ py: 6 }}><Alert severity="warning" action={<Button onClick={() => window.location.reload()}>Retry</Button>}>{onboardingBootstrapError}</Alert><Button color="inherit" onClick={logout} sx={{ mt: 2 }}>Sign out</Button></Container>;

    if (!workspace && busy)
        return <Box role="status" aria-live="polite" sx={{ minHeight: '70vh', display: 'grid', placeItems: 'center' }}><CircularProgress /></Box>;

    if (!workspace)
        return <Container maxWidth="sm" sx={{ py: 6 }}><Alert severity="error" action={<Button onClick={() => void loadWorkspace(true)}>Retry</Button>}>{error}</Alert></Container>;

    if (workspace.Status === 'NeedsRepair' || workspace.Status === 'Abandoned')
        return <Container maxWidth="sm" sx={{ py: 6 }}><Alert severity="warning">This setup workspace cannot be changed right now. Your saved data is intact; contact Synergist support for help resuming it.</Alert><Button sx={{ mt: 2, minHeight: 44 }} onClick={() => history.replace('/')}>Return to dashboard</Button></Container>;

    // Company context becomes an audited organization profile at confirmation;
    // the backend intentionally prevents reopening that provisioning boundary.
    const previousStep = currentIndex > 0 && workspace.CurrentStep !== 'Collaborators'
        ? steps[currentIndex - 1].key : null;

    const saveBeforeExit = async () => {
        setBusy(true);
        setError('');
        try {
            let saved = workspace;
            if (workspace.CurrentStep === 'CompanyProfile'
                && [workspace.Company.Name, workspace.Company.Website, workspace.Company.Description, workspace.Company.Industry, workspace.Company.Size, ...workspace.Company.Values].some(value => value?.trim())) {
                saved = await client.SaveCompanyProfile({
                    ExpectedVersion: saved.Version,
                    IdempotencyKey: createKey(),
                    Name: workspace.Company.Name?.trim() ?? '',
                    Website: workspace.Company.Website,
                    Description: workspace.Company.Description?.trim() ?? '',
                    Industry: workspace.Company.Industry?.trim() ?? '',
                    Size: workspace.Company.Size ?? '',
                    Values: workspace.Company.Values,
                });
            } else if (workspace.CurrentStep === 'FirstPosition'
                && (workspace.PositionDraft.Title?.trim() || workspace.PositionDraft.Description?.trim())) {
                saved = await client.SavePositionDraft({
                    ExpectedVersion: saved.Version,
                    IdempotencyKey: createKey(),
                    Title: workspace.PositionDraft.Title?.trim() ?? '',
                    Description: workspace.PositionDraft.Description?.trim() ?? '',
                    Activities: workspace.PositionDraft.Activities,
                    InteractionId: workspace.PositionDraft.InteractionId,
                });
            }
            setWorkspace(saved);
            track('saved_and_exited', workspace.CurrentStep);
            logout();
        } catch {
            setError('We could not save your latest edits. Stay on this page and retry before signing out.');
        } finally {
            setBusy(false);
        }
    };

    return (
        <Box
            data-testid="onboarding-page"
            data-viewport-mode={viewport.mode}
            sx={{ minHeight: '100dvh', overflowX: 'hidden', bgcolor: 'background.default', pb: 'env(safe-area-inset-bottom)', '& .MuiButton-root': { minHeight: 44 } }}
        >
            {busy && <LinearProgress aria-label="Saving onboarding progress" sx={{ position: 'fixed', top: 0, left: 0, right: 0, zIndex: 1500 }} />}
            <Box component="header" sx={{ borderBottom: 1, borderColor: 'divider', bgcolor: 'background.paper' }}>
                <Container maxWidth="xl" sx={{ py: 1.25 }}>
                    <Stack direction="row" spacing={1.5} alignItems="center" justifyContent="space-between">
                        <Box sx={{ minWidth: 0 }}>
                            <Typography variant="subtitle1" fontWeight={800}>Set up Synergist Pro</Typography>
                            <Typography variant="caption" color="text.secondary">Saved securely as you go</Typography>
                        </Box>
                        <Button
                            color="inherit"
                            startIcon={<SaveRounded />}
                            disabled={busy}
                            onClick={() => void saveBeforeExit()}
                            sx={{ minHeight: 44, flexShrink: 0 }}
                        >
                            {viewport.isPhone ? 'Save & exit' : 'Save and exit'}
                        </Button>
                    </Stack>
                </Container>
            </Box>

            <Container maxWidth="xl" sx={{ py: { xs: 2, md: 4 }, px: { xs: 1.5, sm: 3 } }}>
                <Stack direction={viewport.isDesktop ? 'row' : 'column'} spacing={{ xs: 2, md: 4 }} alignItems="stretch">
                    <Paper
                        component="nav"
                        aria-label="Organization setup progress"
                        variant="outlined"
                        sx={{ width: viewport.isDesktop ? 280 : '100%', p: { xs: 1.25, md: 2 }, flexShrink: 0 }}
                    >
                        {viewport.isPhone ? (
                            <Stack spacing={1}>
                                <Stack direction="row" justifyContent="space-between">
                                    <Typography variant="caption" fontWeight={700}>Step {currentIndex + 1} of {steps.length}</Typography>
                                    <Typography variant="caption" color="text.secondary">{currentMeta.short}</Typography>
                                </Stack>
                                <LinearProgress variant="determinate" value={((currentIndex + 1) / steps.length) * 100} />
                            </Stack>
                        ) : (
                            <Stepper activeStep={currentIndex} orientation={viewport.isDesktop ? 'vertical' : 'horizontal'} alternativeLabel={!viewport.isDesktop}>
                                {steps.map(step => <Step key={step.key}><StepLabel>{viewport.isTablet ? step.short : step.label}</StepLabel></Step>)}
                            </Stepper>
                        )}
                    </Paper>

                    <Paper
                        component="main"
                        variant="outlined"
                        data-testid="onboarding-step-panel"
                        sx={{
                            flex: 1,
                            minWidth: 0,
                            p: { xs: 2, sm: 3, md: 4 },
                            borderRadius: { xs: 2, md: 3 },
                            ...(viewport.isDesktop && workspace.CurrentStep === 'FirstPosition' ? {
                                height: 'calc(100dvh - 144px)',
                                maxHeight: 'calc(100dvh - 144px)',
                                minHeight: 520,
                                overflow: 'hidden',
                            } : {}),
                        }}
                    >
                        <Stack spacing={3} sx={{ height: '100%', minHeight: 0 }}>
                            <Box>
                                <Typography
                                    ref={headingRef}
                                    tabIndex={-1}
                                    component="h1"
                                    variant={viewport.isPhone ? 'h5' : 'h4'}
                                    fontWeight={800}
                                    sx={{ outline: 'none' }}
                                >
                                    {currentMeta.label}
                                </Typography>
                                <Typography color="text.secondary" sx={{ mt: 0.75 }}>
                                    {stepDescription(workspace.CurrentStep)}
                                </Typography>
                            </Box>

                            <Box aria-live="polite" sx={{ position: 'absolute', width: 1, height: 1, overflow: 'hidden', clip: 'rect(0 0 0 0)' }}>
                                {announcement}
                            </Box>
                            {error && <Alert ref={errorRef} tabIndex={-1} severity="warning" action={<Button onClick={() => void loadWorkspace(true)}>Refresh</Button>}>{error}</Alert>}

                            {workspace.CurrentStep === 'CompanyProfile' && <CompanyStep workspace={workspace} setWorkspace={setWorkspace} client={client} busy={busy} setBusy={setBusy} setError={setError} onProvisioned={async (organizationId) => { const switched = await reloadAfterOnboardingProvisioned(organizationId); if (!switched) throw new Error('Organization access is not ready.'); const loaded = await client.GetWorkspace(); if (loaded.OrganizationId !== organizationId) throw new Error('Organization access is not ready.'); preOrganizationMode.current = false; return loaded; }} onNext={() => void goToStep('Collaborators', 'step_completed')} />}
                            {workspace.CurrentStep === 'Collaborators' && <CollaboratorsStep workspace={workspace} setWorkspace={setWorkspace} client={client} busy={busy} setBusy={setBusy} setError={setError} onNext={() => void goToStep('FirstPosition', 'step_completed')} onSkip={() => void goToStep('FirstPosition', 'step_skipped')} />}
                            {workspace.CurrentStep === 'FirstPosition' && <PositionStep workspace={workspace} setWorkspace={setWorkspace} client={client} busy={busy} setBusy={setBusy} setError={setError} onNext={(updated) => goToStep('SuccessProfile', 'step_completed', updated)} onBack={previousStep ? () => void goToStep(previousStep) : undefined} />}
                            {workspace.CurrentStep === 'SuccessProfile' && <SuccessProfileStep workspace={workspace} setWorkspace={setWorkspace} client={client} busy={busy} setBusy={setBusy} setError={setError} onNext={() => void goToStep('Employees', 'step_completed')} />}
                            {workspace.CurrentStep === 'Employees' && <EmployeesStep workspace={workspace} setWorkspace={setWorkspace} client={client} busy={busy} setBusy={setBusy} setError={setError} onNext={() => void goToStep('Activation', 'step_completed')} onSkip={() => void goToStep('Activation', 'step_skipped')} />}
                            {workspace.CurrentStep === 'Activation' && <ActivationStep workspace={workspace} setWorkspace={setWorkspace} client={client} busy={busy} setBusy={setBusy} setError={setError} subscriptionRequired={subscriptionRequired} hasSubscriptionAccess={hasSubscriptionAccess} />}
                            {workspace.CurrentStep === 'Complete' && <SummaryStep workspace={workspace} client={client} busy={busy} setBusy={setBusy} setError={setError} onComplete={() => { track('onboarding_completed', 'Complete'); history.replace('/'); window.location.reload(); }} />}

                            {previousStep && workspace.CurrentStep !== 'Complete' && workspace.CurrentStep !== 'FirstPosition' && (
                                <Box sx={{ pt: 1, borderTop: 1, borderColor: 'divider' }}>
                                    <Button color="inherit" disabled={busy} onClick={() => void goToStep(previousStep)}>Back</Button>
                                </Box>
                            )}
                        </Stack>
                    </Paper>
                </Stack>
            </Container>
        </Box>
    );
};

const CompanyStep = ({ workspace, setWorkspace, client, busy, setBusy, setError, onProvisioned, onNext }) => {
    const decisionKey = useRef(createKey()).current;
    const confirmKey = useRef(createKey()).current;
    const latestWorkspace = useRef(workspace);
    const profileVersion = useRef(workspace.Version);
    const observedProfileVersions = useRef(new Set<string>([workspace.Version]));
    const profileSaveQueue = useRef<Promise<void>>(Promise.resolve());
    const [identity, setIdentity] = useState({
        FirstName: workspace.Identity.FirstName ?? '',
        LastName: workspace.Identity.LastName ?? '',
        Email: workspace.Identity.Email ?? '',
    });
    const valid = Boolean(workspace.Company.Name?.trim() && workspace.Company.Description?.trim() && workspace.Company.Industry && workspace.Company.Size);
    useEffect(() => {
        latestWorkspace.current = workspace;
    }, [workspace]);
    useEffect(() => {
        // API responses advance the queue-owned token synchronously. Only adopt
        // an unseen prop token here, which represents an explicit server reload;
        // a late React effect must never move the queue back to an older token.
        if (!observedProfileVersions.current.has(workspace.Version)) {
            observedProfileVersions.current.add(workspace.Version);
            profileVersion.current = workspace.Version;
        }
    }, [workspace.Version]);

    const adoptWorkspace = (updated) => {
        observedProfileVersions.current.add(updated.Version);
        profileVersion.current = updated.Version;
        latestWorkspace.current = updated;
        setWorkspace(updated);
        return updated;
    };

    const update = (field: string, value: any) => setWorkspace(current => {
        const updated = {
            ...current!,
            Company: { ...current!.Company, [field]: value },
            AiSummary: { ...current!.AiSummary, Status: 'None' as const, Summary: undefined },
        };
        latestWorkspace.current = updated;
        return updated;
    });

    const saveIdentity = async () => {
        setBusy(true); setError('');
        try {
            const updated = await client.SaveIdentityProfile({
                ExpectedVersion: profileVersion.current, IdempotencyKey: createKey(), ...identity,
            });
            adoptWorkspace(updated);
        } catch { setError('Your contact profile could not be saved. Check the fields and try again.'); }
        finally { setBusy(false); }
    };

    const saveProfile = async (expectedVersion = workspace.Version, company = workspace.Company) => client.SaveCompanyProfile({
        ExpectedVersion: expectedVersion,
        IdempotencyKey: createKey(),
        Name: company.Name?.trim() ?? '',
        Website: company.Website,
        Description: company.Description?.trim() ?? '',
        Industry: company.Industry?.trim() ?? '',
        Size: company.Size ?? '',
        Values: company.Values,
    });

    const persistPartialProfile = () => {
        if (latestWorkspace.current.OrganizationId)
            return;
        const company = {
            ...latestWorkspace.current.Company,
            Values: [...latestWorkspace.current.Company.Values],
        };
        profileSaveQueue.current = profileSaveQueue.current.then(async () => {
            const saved = await saveProfile(profileVersion.current, company);
            // A user can already be typing in the next field while this request
            // returns. Advance the ref synchronously so the next queued save uses
            // the returned version even before React commits this state update.
            observedProfileVersions.current.add(saved.Version);
            profileVersion.current = saved.Version;
            latestWorkspace.current = { ...saved, Company: latestWorkspace.current.Company };
            setWorkspace(current => {
                const updated = { ...saved, Company: current!.Company };
                latestWorkspace.current = updated;
                return updated;
            });
        }).catch(() => {
            setError('Your latest company edits are still on screen but could not be saved. Retry before leaving this step.');
        });
    };

    const generate = async () => {
        if (!valid)
            return;
        setBusy(true); setError('');
        try {
            await profileSaveQueue.current;
            const current = latestWorkspace.current;
            const saved = adoptWorkspace(await saveProfile(profileVersion.current, current.Company));
            const updated = await client.GenerateCompanyAiSummary({ ExpectedVersion: saved.Version, IdempotencyKey: createKey() });
            adoptWorkspace(updated); track('ai_generated', 'CompanyProfile');
        } catch { setError('The company summary could not be drafted. Your company details are still here; try again.'); }
        finally { setBusy(false); }
    };

    const approve = async () => {
        setBusy(true); setError('');
        try {
            const decided = await client.DecideCompanyAiSummary({ ExpectedVersion: profileVersion.current, IdempotencyKey: decisionKey, Accept: true });
            adoptWorkspace(decided);
            const updated = await client.ConfirmCompanyProfile({ ExpectedVersion: decided.Version, IdempotencyKey: confirmKey });
            adoptWorkspace(updated); track('ai_accepted', 'CompanyProfile');
            setWorkspace(await onProvisioned(updated.OrganizationId));
        } catch {
            try {
                if (!latestWorkspace.current.OrganizationId) throw new Error('Organization has not been provisioned.');
                setWorkspace(await onProvisioned(latestWorkspace.current.OrganizationId)); return;
            }
            catch { setError('The summary is saved, but organization access is not ready. Retry safely; the request will not create a duplicate.'); }
        }
        finally { setBusy(false); }
    };

    const confirmAcceptedContext = async () => {
        setBusy(true); setError('');
        try {
            const updated = await client.ConfirmCompanyProfile({ ExpectedVersion: profileVersion.current, IdempotencyKey: confirmKey });
            adoptWorkspace(updated);
            setWorkspace(await onProvisioned(updated.OrganizationId));
        } catch {
            try {
                if (!latestWorkspace.current.OrganizationId) throw new Error('Organization has not been provisioned.');
                setWorkspace(await onProvisioned(latestWorkspace.current.OrganizationId)); return;
            }
            catch { setError('The approved context is safe, but organization access is not ready. Retry when your connection is stable.'); }
        }
        finally { setBusy(false); }
    };

    const continueWithoutAi = async () => {
        if (!valid)
            return;
        setBusy(true); setError('');
        try {
            await profileSaveQueue.current;
            const current = latestWorkspace.current;
            let updated = adoptWorkspace(await saveProfile(profileVersion.current, current.Company));
            if (updated.AiSummary.Status === 'Draft') {
                updated = await client.DecideCompanyAiSummary({ ExpectedVersion: updated.Version, IdempotencyKey: createKey(), Accept: false });
                adoptWorkspace(updated);
                track('ai_rejected', 'CompanyProfile');
            }
            updated = await client.ConfirmCompanyProfile({ ExpectedVersion: updated.Version, IdempotencyKey: confirmKey });
            adoptWorkspace(updated);
            setWorkspace(await onProvisioned(updated.OrganizationId));
        } catch {
            try {
                if (!latestWorkspace.current.OrganizationId) throw new Error('Organization has not been provisioned.');
                setWorkspace(await onProvisioned(latestWorkspace.current.OrganizationId)); return;
            }
            catch { setError('The company details are saved, but organization access is not ready. Retry safely to continue.'); }
        }
        finally { setBusy(false); }
    };

    return <Stack spacing={2.25}>
        {workspace.Identity.RequiresCompletion && <Paper variant="outlined" sx={{ p: 2 }}>
            <Stack spacing={1.5}>
                <Typography fontWeight={800}>Complete your contact profile</Typography>
                <Typography variant="body2" color="text.secondary">Microsoft did not provide all required contact details. Confirm them before creating the organization.</Typography>
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5}>
                    <TextField required label="Your first name" value={identity.FirstName} onChange={e => setIdentity({ ...identity, FirstName: e.target.value })} fullWidth />
                    <TextField required label="Your last name" value={identity.LastName} onChange={e => setIdentity({ ...identity, LastName: e.target.value })} fullWidth />
                </Stack>
                <TextField required type="email" label="Your work email" value={identity.Email} onChange={e => setIdentity({ ...identity, Email: e.target.value })} fullWidth />
                <Button variant="contained" disabled={busy || !identity.FirstName.trim() || !identity.LastName.trim() || !/^\S+@\S+\.\S+$/.test(identity.Email)} onClick={() => void saveIdentity()} sx={{ alignSelf: 'flex-end' }}>Save contact profile</Button>
            </Stack>
        </Paper>}
        <Alert severity="info">This context grounds future AI suggestions. You approve the final summary before an organization is created.</Alert>
        <TextField required label="Company name" value={workspace.Company.Name ?? ''} onChange={e => update('Name', e.target.value)} onBlur={() => void persistPartialProfile()} disabled={workspace.Identity.RequiresCompletion} fullWidth />
        <TextField label="Website (optional)" type="url" value={workspace.Company.Website ?? ''} onChange={e => update('Website', e.target.value)} onBlur={() => void persistPartialProfile()} fullWidth />
        <TextField required label="What does your company do?" value={workspace.Company.Description ?? ''} onChange={e => update('Description', e.target.value)} onBlur={() => void persistPartialProfile()} multiline minRows={3} fullWidth />
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
            <TextField required label="Industry" value={workspace.Company.Industry ?? ''} onChange={e => update('Industry', e.target.value)} onBlur={() => void persistPartialProfile()} fullWidth />
            <TextField select required label="Company size" value={workspace.Company.Size ?? ''} onChange={e => update('Size', e.target.value)} onBlur={() => void persistPartialProfile()} fullWidth>
                {['1–9', '10–49', '50–249', '250–999', '1,000+'].map(size => <MenuItem key={size} value={size}>{size} employees</MenuItem>)}
            </TextField>
        </Stack>
        <TextField label="Values or cultural priorities (optional)" helperText="Separate values with commas" value={workspace.Company.Values.join(', ')} onChange={e => update('Values', e.target.value.split(',').map(x => x.trim()).filter(Boolean))} onBlur={() => void persistPartialProfile()} fullWidth />
        {!workspace.AiSummary.Summary && <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}><Button variant="contained" startIcon={<AutoAwesomeRounded />} disabled={busy || !valid || workspace.Identity.RequiresCompletion} onClick={() => void generate()} sx={{ minHeight: 44 }}>Draft organization context</Button><Button disabled={busy || !valid || workspace.Identity.RequiresCompletion} onClick={() => void continueWithoutAi()}>Continue without AI</Button></Stack>}
        {workspace.AiSummary.Summary && <Paper variant="outlined" sx={{ p: 2 }}>
            <Stack spacing={1.5}>
                <Stack direction="row" justifyContent="space-between"><Typography fontWeight={700}>AI-drafted organization context</Typography><Chip label="Draft" size="small" /></Stack>
                <Typography sx={{ whiteSpace: 'pre-wrap' }}>{workspace.AiSummary.Summary}</Typography>
                <Typography variant="caption" color="text.secondary">Review this carefully. Approved context will guide job descriptions, coaching frameworks, and future AI conversations.</Typography>
                {workspace.AiSummary.Status !== 'Accepted' && <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} justifyContent="flex-end"><Button onClick={() => void generate()} disabled={busy}>Redraft</Button><Button onClick={() => void continueWithoutAi()} disabled={busy}>Use my description instead</Button><Button variant="contained" onClick={() => void approve()} disabled={busy}>Approve context</Button></Stack>}
                {workspace.AiSummary.Status === 'Accepted' && <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} justifyContent="space-between" alignItems={{ sm: 'center' }}><Chip color="success" icon={<CheckCircleRounded />} label="Approved" sx={{ alignSelf: 'flex-start' }} />{!workspace.OrganizationId && <Button variant="contained" disabled={busy} onClick={() => void confirmAcceptedContext()}>Create organization with approved context</Button>}</Stack>}
            </Stack>
        </Paper>}
        {workspace.OrganizationId && <Button variant="contained" disabled={busy} onClick={onNext} sx={{ minHeight: 44, alignSelf: 'flex-end' }}>Continue</Button>}
    </Stack>;
};

const CollaboratorsStep = ({ workspace, setWorkspace, client, busy, setBusy, setError, onNext, onSkip }) => {
    const [email, setEmail] = useState('');
    const [role, setRole] = useState<CollaboratorRole>('Supervisor');
    const invite = async () => {
        setBusy(true); setError('');
        try {
            const updated = await client.InviteCollaborator({ ExpectedVersion: workspace.Version, IdempotencyKey: createKey(), Email: email.trim(), Role: role });
            setWorkspace(updated); setEmail(''); track('invite_sent', 'Collaborators');
        } catch { setError('That invitation could not be sent. Check the address and try again.'); }
        finally { setBusy(false); }
    };
    return <Stack spacing={2.5}>
        <Alert severity="info" icon={<GroupsRounded />}><strong>Setup collaborators can sign in and help configure Synergist.</strong> They are not Talent being developed and do not count as active Talent for billing.</Alert>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} alignItems={{ sm: 'flex-start' }}>
            <TextField label="Work email" type="email" value={email} onChange={e => setEmail(e.target.value)} fullWidth />
            <FormControl sx={{ minWidth: { sm: 180 } }}><InputLabel id="collaborator-role-label">Access</InputLabel><Select labelId="collaborator-role-label" label="Access" value={role} onChange={e => setRole(e.target.value as CollaboratorRole)}><MenuItem value="Supervisor">Supervisor</MenuItem><MenuItem value="Administrator">Administrator</MenuItem></Select></FormControl>
            <Button variant="contained" startIcon={<SendRounded />} disabled={busy || !/^\S+@\S+\.\S+$/.test(email.trim())} onClick={() => void invite()} sx={{ minHeight: 56, flexShrink: 0 }}>Send invite</Button>
        </Stack>
        {workspace.Collaborators.length > 0 && <Stack spacing={1} aria-label="Invited setup collaborators">{workspace.Collaborators.map(item => <Paper key={`${item.Email}-${item.Role}`} variant="outlined" sx={{ p: 1.5 }}><Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} justifyContent="space-between" alignItems={{ sm: 'center' }}><Box sx={{ minWidth: 0 }}><Typography fontWeight={650} sx={{ overflowWrap: 'anywhere' }}>{item.Email}</Typography><Typography variant="caption" color="text.secondary">{item.Role} · setup collaborator</Typography></Box><Chip label={item.Status} size="small" sx={{ alignSelf: 'flex-start' }} /></Stack></Paper>)}</Stack>}
        <Stack direction={{ xs: 'column-reverse', sm: 'row' }} spacing={1} justifyContent="flex-end"><Button disabled={busy} onClick={onSkip}>Skip for now</Button><Button variant="contained" disabled={busy} onClick={onNext}>Continue</Button></Stack>
    </Stack>;
};

export const PositionStep = ({ workspace, setWorkspace, client, busy, setBusy, setError, onNext, onBack }) => {
    const viewport = useViewportMode();
    const activitySummary = workspace.PositionDraft.Activities ?? '';
    const [interactionId, setInteractionId] = useState(workspace.PositionDraft.InteractionId ?? '');
    const [messages, setMessages] = useState<AiChatMessage[]>([]);
    const [aiBusy, setAiBusy] = useState(false);
    const [aiDialogOpen, setAiDialogOpen] = useState(false);
    const positionWorkspaceRef = useRef<HTMLDivElement | null>(null);
    const createPositionKey = useRef(createKey()).current;
    const latestWorkspace = useRef(workspace);
    const draftSavePromise = useRef<Promise<OnboardingWorkspace> | null>(null);
    latestWorkspace.current = workspace;
    useEffect(() => {
        if (!workspace.PositionDraft.InteractionId)
            return;
        let active = true;
        client.GetPositionDraftConversation()
            .then(conversation => {
                if (!active)
                    return;
                setInteractionId(conversation.InteractionId);
                setMessages(conversation.Messages ?? []);
            })
            .catch(() => setError('The position draft is restored, but its AI conversation could not be loaded. You can continue editing manually.'));
        return () => { active = false; };
    }, []); // eslint-disable-line react-hooks/exhaustive-deps
    const adoptWorkspace = (updated: OnboardingWorkspace) => {
        latestWorkspace.current = updated;
        setWorkspace(updated);
        return updated;
    };
    const adoptDraftResponse = (
        authoritative: OnboardingWorkspace,
        request: { Title: string; Description: string; Activities?: string },
    ) => {
        const localDraft = latestWorkspace.current.PositionDraft;
        const merged = {
            ...authoritative,
            PositionDraft: {
                ...authoritative.PositionDraft,
                Title: localDraft.Title !== request.Title ? localDraft.Title : authoritative.PositionDraft.Title,
                Description: localDraft.Description !== request.Description ? localDraft.Description : authoritative.PositionDraft.Description,
                Activities: localDraft.Activities !== request.Activities ? localDraft.Activities : authoritative.PositionDraft.Activities,
            },
        };
        adoptWorkspace(merged);
        return authoritative;
    };
    const update = (field: string, value: string) => setWorkspace(current => {
        const updated = { ...current!, PositionDraft: { ...current!.PositionDraft, [field]: value } };
        latestWorkspace.current = updated;
        return updated;
    });
    const saveDraft = (draft: { Title: string; Description: string; Activities?: string }) => {
        const previous = draftSavePromise.current;
        const operation = (async () => {
            if (previous) {
                try { await previous; } catch { /* The queued retry still uses the last authoritative version. */ }
            }
            const current = latestWorkspace.current;
            const saved = await client.SavePositionDraft({
                ExpectedVersion: current.Version,
                IdempotencyKey: createKey(),
                Title: draft.Title,
                Description: draft.Description,
                Activities: draft.Activities,
                InteractionId: interactionId || undefined,
            });
            return adoptDraftResponse(saved, draft);
        })();
        draftSavePromise.current = operation;
        void operation.finally(() => {
            if (draftSavePromise.current === operation)
                draftSavePromise.current = null;
        }).catch(() => undefined);
        return operation;
    };
    const persistDraft = async () => {
        if (!workspace.PositionDraft.Title?.trim())
            return;
        try {
            await saveDraft({ Title: workspace.PositionDraft.Title.trim(), Description: workspace.PositionDraft.Description ?? '', Activities: activitySummary });
        } catch { setError('Your latest position edits are still on screen but could not be saved. Retry before leaving this step.'); }
    };
    const sendPrompt = async (override?: string, messageHistory?: AiChatMessage[]) => {
        const userMessage = (override ?? '').trim();
        if (!workspace.PositionDraft.Title?.trim() || !userMessage || aiBusy)
            return null;
        const next = messageHistory ?? [...messages, { role: 'user' as const, content: userMessage }];
        if (!messageHistory)
            setMessages(next);
        setAiBusy(true); setError('');
        try {
            const draft = await saveDraft({ Title: workspace.PositionDraft.Title, Description: workspace.PositionDraft.Description ?? '', Activities: activitySummary });
            const contextSummary = recallContextSummary(`position:${draft.PositionDraft.PositionId ?? workspace.Id}`) || activitySummary;
            const contextualResponse = await new AiTemplateClient().ContinueContextualChat(buildContextualRequest('onboarding-position', 'generate-now', {
                PositionTitle: draft.PositionDraft.Title!,
                JobDescription: draft.PositionDraft.Description ?? '',
                Answers: activitySummary.trim() ? [{ GapKey: 'RoleOutcomesAndFailureCosts', Answer: activitySummary.trim() }] : [],
                Messages: next.map(({ role, content }) => ({ role, content })),
                PositionId: draft.PositionDraft.PositionId,
                InteractionId: draft.PositionDraft.InteractionId || undefined,
                ContextSummary: contextSummary,
                GenerateNow: true,
            }));
            const response = contextualResponse.PositionDescription;
            if (!response)
                throw new Error('Position assistant did not return a description.');
            const aiDraft = { Title: draft.PositionDraft.Title!, Description: response.UpdatedDescription, Activities: activitySummary };
            const saved = await client.SavePositionDraft({ ExpectedVersion: draft.Version, IdempotencyKey: createKey(), ...aiDraft, InteractionId: contextualResponse.InteractionId });
            adoptDraftResponse(saved, aiDraft);
            setInteractionId(contextualResponse.InteractionId);
            setMessages([...next, { role: 'assistant', content: response.AssistantMessage }]); track('ai_generated', 'FirstPosition');
            rememberContextSummary(`position:${draft.PositionDraft.PositionId ?? workspace.Id}`, contextualResponse.ContextSummary || contextSummary);
            return response.AssistantMessage;
        } catch { setError('The job description assistant could not respond. Nothing was saved; please retry.'); return null; }
        finally { setAiBusy(false); }
    };
    const generateInitial = () => {
        setAiDialogOpen(true);
        void sendPrompt(`Draft a job description. The main activities and responsibilities are: ${activitySummary}`);
    };
    const savePosition = async () => {
        setBusy(true); setError('');
        try {
            const saved = await saveDraft({ Title: workspace.PositionDraft.Title!.trim(), Description: workspace.PositionDraft.Description!.trim(), Activities: activitySummary });
            let updated: OnboardingWorkspace;
            try {
                updated = await client.CreateFirstPosition({ ExpectedVersion: saved.Version, IdempotencyKey: createPositionKey, Title: saved.PositionDraft.Title!.trim(), Description: saved.PositionDraft.Description!.trim() });
            } catch (error) {
                // The draft save is authoritative even when creation fails or its response
                // is lost. Reload once so a successful idempotent creation is discovered;
                // otherwise retain the saved draft/version for a safe retry.
                const persistedDraft = { Title: saved.PositionDraft.Title!, Description: saved.PositionDraft.Description!, Activities: saved.PositionDraft.Activities };
                try { adoptDraftResponse(await client.GetWorkspace(), persistedDraft); }
                catch { adoptDraftResponse(saved, persistedDraft); }
                throw error;
            }
            adoptWorkspace(updated); track('position_created', 'FirstPosition');
            setAiDialogOpen(false);
            await onNext(updated);
        } catch { setError('The position could not be saved. No duplicate was created; please try again.'); }
        finally { setBusy(false); }
    };
    return <Stack spacing={0} sx={{ flex: viewport.isDesktop ? '1 1 auto' : undefined, minHeight: 0, overflow: viewport.isDesktop ? 'hidden' : 'visible' }}>
        <Stack ref={positionWorkspaceRef} data-testid="onboarding-position-scroll-region" spacing={2.25} sx={{ flex: viewport.isDesktop ? '1 1 auto' : undefined, minHeight: 0, overflowY: viewport.isDesktop ? 'auto' : 'visible', pr: viewport.isDesktop ? 1 : 0 }}>
        <TextField required label="Position title" value={workspace.PositionDraft.Title ?? ''} onChange={e => update('Title', e.target.value)} onBlur={() => void persistDraft()} disabled={Boolean(workspace.PositionDraft.PositionId)} fullWidth />
        <TextField required label="Main activities and responsibilities" value={activitySummary} onChange={e => update('Activities', e.target.value)} onBlur={() => void persistDraft()} multiline minRows={3} disabled={Boolean(workspace.PositionDraft.PositionId)} fullWidth />
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems={{ sm: 'center' }}>
            <Button variant="outlined" startIcon={<AutoAwesomeRounded />} disabled={aiBusy || !workspace.PositionDraft.Title?.trim() || !activitySummary.trim()} onClick={generateInitial} sx={{ minHeight: 44 }}>{workspace.PositionDraft.Description ? 'Redraft with AI' : 'Draft with AI (optional)'}</Button>
            <Button variant="text" startIcon={<AutoAwesomeRounded />} disabled={!workspace.PositionDraft.Title?.trim()} onClick={() => setAiDialogOpen(true)} sx={{ minHeight: 44 }}>Open Synergist AI</Button>
        </Stack>
        <TextField label="Job description" value={workspace.PositionDraft.Description ?? ''} onChange={e => update('Description', e.target.value)} onBlur={() => void persistDraft()} multiline minRows={14} fullWidth />
        <ContextualAiDialog open={aiDialogOpen} onClose={() => setAiDialogOpen(false)} anchorEl={positionWorkspaceRef.current} title="Synergist AI — Position Description" workflow="position-generation">
            <ContextualAiWorkspace
                adapter={{
                    workflow: 'onboarding-position',
                    sessionKey: `onboarding-position:${workspace.PositionDraft.PositionId ?? workspace.Id}`,
                    voiceWorkflow: 'PositionDescription',
                    contextId: workspace.PositionDraft.PositionId ?? null,
                    capabilities: { freeText: true, voice: true, editableContextSummary: true },
                    initialMessages: messages.filter(message => message.role !== 'system').map(message => ({ role: message.role as 'user' | 'assistant', content: message.content })),
                    initialContextSummary: recallContextSummary(`position:${workspace.PositionDraft.PositionId ?? workspace.Id}`),
                    progressLabel: 'Refining position',
                    submit: async request => {
                        const assistantMessage = await sendPrompt(request.text, request.messages);
                        if (!assistantMessage)
                            throw new Error('Position assistant did not respond');
                        return { assistantMessage, result: latestWorkspace.current.PositionDraft.Description ?? '' };
                    },
                } as ContextualAiWorkflowAdapter<string>}
                testId="onboarding-position-ai-workspace"
                transcriptTestId="onboarding-position-chat-scroll"
                composerTestId="onboarding-position-ai-composer"
                composerLabel="Ask for a change"
                onMessagesChange={next => setMessages(next)}
                onContextSummaryChange={summary => rememberContextSummary(`position:${workspace.PositionDraft.PositionId ?? workspace.Id}`, summary)}
            />
        </ContextualAiDialog>
        </Stack>
        <Stack data-testid="onboarding-position-actions" direction={{ xs: 'column-reverse', sm: 'row' }} spacing={1} justifyContent="space-between" sx={{ flex: '0 0 auto', mt: 2, pt: 1.5, borderTop: 1, borderColor: 'divider', bgcolor: 'background.paper' }}>
            <Box>{onBack && <Button color="inherit" disabled={busy} onClick={onBack}>Back</Button>}</Box>
            <Box>{!workspace.PositionDraft.PositionId && <Button variant="contained" disabled={busy || !workspace.PositionDraft.Title?.trim() || !workspace.PositionDraft.Description?.trim()} onClick={() => void savePosition()}>Save and Continue</Button>}{workspace.PositionDraft.PositionId && <Button variant="contained" onClick={() => void onNext(workspace)}>Continue</Button>}</Box>
        </Stack>
    </Stack>;
};

export const SuccessProfileStep = ({ workspace, setWorkspace, client, busy, setBusy, setError, onNext }) => {
    const [templateName] = useState(`${workspace.PositionDraft.Title ?? 'Position'} Success Profile`);
    const [aiBusy, setAiBusy] = useState(false);
    const [aiDialogOpen, setAiDialogOpen] = useState(false);
    const discoveryIndex = 0;
    const [aiMessages, setAiMessages] = useState<AiChatMessage[]>([]);
    const [contextAnswers, setContextAnswers] = useState<AiContextualAiAnswer[]>([]);
    const [currentGap, setCurrentGap] = useState<AiContextualAiAnswer['GapKey'] | undefined>();
    const [coverageConfirmations, setCoverageConfirmations] = useState<AiTemplateCoverageConfirmation[]>([]);
    const [coverageContractVersion, setCoverageContractVersion] = useState<number | undefined>();
    const [changedCoverageGroups, setChangedCoverageGroups] = useState<string[]>([]);
    const [coverageReady, setCoverageReady] = useState(workspace.SuccessProfile.Proposals.length > 0);
    const [manualOpen, setManualOpen] = useState(false);
    const [manualGroups, setManualGroups] = useState<Record<string, string>>(() => Object.fromEntries(requiredSuccessGroups.map(group => [group, ''])));
    const successProfileWorkspaceRef = useRef<HTMLDivElement | null>(null);
    const generationKey = useRef(createKey());
    const approvalKey = useRef(createKey()).current;
    const decisionKeys = useRef<Record<string, { decision: 'Accepted' | 'Rejected'; key: string }>>({});
    const templateResource = workspace.Resources.find(item => item.Type === 'Template');
    const preview = workspace.SuccessProfile.Preview;
    const proposals = workspace.SuccessProfile.Proposals;
    const discoveryQuestions = buildTemplateDiscoveryInterview({
        jobDescription: workspace.PositionDraft.Description ?? '',
        knownContext: recallContextSummary(`position:${workspace.PositionDraft.PositionId ?? workspace.Id}`),
    });
    const decide = async (id: string, decision: 'Accepted' | 'Rejected') => {
        const changedGroup = workspace.SuccessProfile.Proposals.find(item => item.Id === id)?.Group;
        // Any change to the effective included set invalidates the server's prior
        // coverage result immediately. Keep every affected category outstanding
        // until one fresh ReviewCoverage turn validates the complete set.
        setCoverageReady(false);
        if (changedGroup) {
            setCoverageConfirmations(current => current.filter(item => item.ProficiencyGroup !== changedGroup));
            setChangedCoverageGroups(current => current.includes(changedGroup) ? current : [...current, changedGroup]);
        }
        setAiBusy(true); setError('');
        try {
            if (decisionKeys.current[id]?.decision !== decision)
                decisionKeys.current[id] = { decision, key: createKey() };
            const updated = await client.DecideSuccessProfileProposal({ ExpectedVersion: workspace.Version, IdempotencyKey: decisionKeys.current[id].key, ProposalId: id, Accept: decision === 'Accepted' });
            setWorkspace(updated);
            track(decision === 'Accepted' ? 'ai_accepted' : 'ai_rejected', 'SuccessProfile');
        } catch { setError('That decision was not saved. Refresh the authoritative proposal list before retrying.'); }
        finally { setAiBusy(false); }
    };
    const createTemplate = async () => {
        if (!coverageReady || changedCoverageGroups.length > 0) {
            setError('Review the updated success-profile coverage with Synergist AI before saving.');
            return;
        }
        setBusy(true); setError('');
        try {
            let authoritative = workspace;
            for (const proposal of authoritative.SuccessProfile.Proposals.filter(item => item.Decision === 'Pending')) {
                if (decisionKeys.current[proposal.Id]?.decision !== 'Accepted')
                    decisionKeys.current[proposal.Id] = { decision: 'Accepted', key: createKey() };
                authoritative = await client.DecideSuccessProfileProposal({ ExpectedVersion: authoritative.Version, IdempotencyKey: decisionKeys.current[proposal.Id].key, ProposalId: proposal.Id, Accept: true });
            }
            const updated = await client.ApproveSuccessProfile({ ExpectedVersion: authoritative.Version, IdempotencyKey: approvalKey });
            setWorkspace(updated); track('template_created', 'SuccessProfile');
            setAiDialogOpen(false);
        } catch { setError('The success profile could not be saved. Review the pending decisions and try again.'); }
        finally { setBusy(false); }
    };
    const manualValidationIssue = manualProfileValidationIssue(manualGroups);
    const manualValid = !manualValidationIssue;
    const saveManual = async () => {
        if (!manualValid)
            return;
        setBusy(true); setError('');
        try {
            const manualPreview: AiTemplatePreview = {
                PositionId: workspace.PositionDraft.PositionId!,
                TemplateName: templateName,
                ProcessType: ProcessType.Evaluate,
                JobDescription: workspace.PositionDraft.Description ?? '',
                InteractionId: workspace.SuccessProfile.InteractionId ?? '00000000-0000-0000-0000-000000000000',
                AssistantMessage: 'Manual success profile saved for review.',
                ProficiencyGroups: requiredSuccessGroups.map(group => ({ ProficiencyGroup: group, Weight: 5, Proficiencies: parseManualSuccessItems(manualGroups[group]) })),
            };
            const updated = await client.SaveManualSuccessProfile({ ExpectedVersion: workspace.Version, IdempotencyKey: createKey(), Preview: manualPreview });
            setWorkspace(updated); setCoverageReady(true); setManualOpen(false); track('manual_success_profile_saved', 'SuccessProfile');
        } catch { setError('The manual success profile could not be saved. Your entries remain on screen; review the category requirements and retry.'); }
        finally { setBusy(false); }
    };
    return <Stack ref={successProfileWorkspaceRef} spacing={2.25}>
        <Alert severity="info">Synergist AI leads with strengths, abilities, and observable positive practices. Suggestions are included by default; deny only a criterion that does not belong. Counterproductive behaviors stay sparse and diagnostic, never punitive.</Alert>
        {proposals.length === 0 && <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}><Button variant="contained" startIcon={<AutoAwesomeRounded />} disabled={aiBusy} onClick={() => setAiDialogOpen(true)} sx={{ minHeight: 44 }}>Discuss what success looks like</Button><Button variant="outlined" disabled={busy} onClick={() => setManualOpen(current => !current)}>{manualOpen ? 'Hide manual editor' : 'Build manually'}</Button></Stack>}
        {manualOpen && proposals.length === 0 && <Paper variant="outlined" sx={{ p: { xs: 1.5, sm: 2 } }}><Stack spacing={2}><Box><Typography fontWeight={800}>Manual success profile</Typography><Typography variant="body2" color="text.secondary">For each category, enter distinct <strong>Ability:</strong> and <strong>Behavior+:</strong> lines until you believe the role has sufficient meaningful coverage. A category does not need a fixed number. Use <strong>Behavior-:</strong> only for a consequential observable pattern; counterproductive behaviors are optional, sparse, and may not exceed one in five items across the whole profile. Your explicit save includes these user-authored items.</Typography></Box>{Object.values(manualGroups).some(value => value.trim()) && manualValidationIssue && <Alert severity="warning">{manualValidationIssue}</Alert>}{requiredSuccessGroups.map(group => { const count = parseManualSuccessItems(manualGroups[group]).length; return <TextField key={group} label={group} value={manualGroups[group]} onChange={event => setManualGroups(current => ({ ...current, [group]: event.target.value }))} helperText={manualGroups[group] && !manualGroupIsValid(manualGroups[group]) ? 'Use at least one distinct Ability:, Behavior+:, or Behavior-: line.' : `${count} distinct ${count === 1 ? 'item' : 'items'} · you decide when coverage is sufficient`} error={Boolean(manualGroups[group]) && !manualGroupIsValid(manualGroups[group])} multiline minRows={5} fullWidth />; })}<Button variant="contained" disabled={busy || !manualValid} onClick={() => void saveManual()} sx={{ alignSelf: { xs: 'stretch', sm: 'flex-end' } }}>Save manual profile</Button></Stack></Paper>}
        {preview?.AssistantMessage && <Alert severity="success">{preview.AssistantMessage}</Alert>}
        {proposals.length > 0 && <Stack spacing={1.25} aria-label="AI success profile proposals">{proposals.map(item => <Paper key={item.Id} variant="outlined" sx={{ p: 1.5, opacity: item.Decision === 'Rejected' ? 0.55 : 1 }}><Stack spacing={1}><Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} justifyContent="space-between"><Box><Typography fontWeight={700}>{item.Description}</Typography><Typography variant="caption" color="text.secondary">{item.Group} · {item.Type}{item.IsNegative ? ' · observable development opportunity' : ''}</Typography></Box><Chip size="small" label={item.Decision === 'Rejected' ? 'Denied' : 'Included'} sx={{ alignSelf: 'flex-start' }} /></Stack><Stack direction="row" justifyContent="flex-end">{item.Decision === 'Rejected' ? <Button disabled={aiBusy} onClick={() => void decide(item.Id, 'Accepted')}>Undo denial</Button> : <Button color="inherit" disabled={aiBusy} onClick={() => void decide(item.Id, 'Rejected')}>Deny</Button>}</Stack></Stack></Paper>)}</Stack>}
        {proposals.length > 0 && <Button variant="outlined" startIcon={<AutoAwesomeRounded />} onClick={() => setAiDialogOpen(true)} sx={{ alignSelf: { xs: 'stretch', sm: 'flex-end' } }}>Open Synergist AI</Button>}
        <ContextualAiDialog open={aiDialogOpen} onClose={() => setAiDialogOpen(false)} anchorEl={successProfileWorkspaceRef.current} title="Synergist AI — Success Profile" workflow="success-profile-generation">
            <ContextualAiWorkspace
                adapter={{
                    workflow: 'onboarding-success-profile',
                    sessionKey: `onboarding-success:${workspace.PositionDraft.PositionId ?? workspace.Id}`,
                    voiceWorkflow: 'TemplateCreation',
                    contextId: workspace.PositionDraft.PositionId,
                    capabilities: { choices: true, freeText: true, skip: !preview, generateNow: !preview, voice: true, editableContextSummary: true },
                    initialMessages: aiMessages.filter(message => message.role !== 'system').map(message => ({ role: message.role as 'user' | 'assistant', content: message.content })),
                    initialContextSummary: recallContextSummary(`position:${workspace.PositionDraft.PositionId ?? workspace.Id}`),
                    initialQuestion: !preview ? discoveryQuestions[discoveryIndex]?.prompt : undefined,
                    initialChoices: !preview ? discoveryQuestions[discoveryIndex]?.choices : undefined,
                    progressLabel: preview ? 'Refining success profile' : `Discovery ${Math.min(discoveryIndex + 1, discoveryQuestions.length)} of ${discoveryQuestions.length}`,
                    submit: async request => {
                        const initialGap = discoveryQuestions[discoveryIndex]?.topic
                            ? onboardingDiscoveryTopicGap[discoveryQuestions[discoveryIndex].topic]
                            : undefined;
                        const answeredGap = request.action === 'answer' && request.text?.trim()
                            ? currentGap ?? initialGap
                            : undefined;
                        const nextAnswers = answeredGap
                            ? [...contextAnswers.filter(item => item.GapKey !== answeredGap), { GapKey: answeredGap, Answer: request.text!.trim() }]
                            : contextAnswers;
                        if (answeredGap)
                            setContextAnswers(nextAnswers);

                        const confirmation = request.action === 'coverage-confirm-sufficient'
                            ? {
                                ProficiencyGroup: String(request.metadata?.category ?? ''),
                                CoverageFingerprint: String(request.metadata?.coverageFingerprint ?? ''),
                                ConfirmedSufficient: true,
                                ContextSummary: request.contextSummary.trim() || 'Success-profile coverage reviewed with Synergist AI.',
                            }
                            : null;
                        const nextConfirmations = confirmation
                            ? [...coverageConfirmations.filter(item => item.ProficiencyGroup !== confirmation.ProficiencyGroup), confirmation]
                            : coverageConfirmations;
                        const requestVersion = Number(request.metadata?.coverageContractVersion) || coverageContractVersion;

                        const coverageCategory = String(request.metadata?.category ?? '');
                        const suggestions = Array.isArray(request.metadata?.suggestions)
                            ? request.metadata!.suggestions as Array<{ label?: string }>
                            : [];
                        const coverageInstruction = request.action === 'coverage-add-suggestions'
                            ? `Add these role-specific criteria to ${coverageCategory}: ${suggestions.map(item => item.label).filter(Boolean).join('; ')}`
                            : request.action === 'coverage-describe-missing'
                                ? `Improve ${coverageCategory} coverage based on this guidance: ${request.text ?? ''}`
                                : request.action === 'coverage-regenerate'
                                    ? `Explore ${coverageCategory} further and replace shallow or duplicative criteria with distinct role-specific coverage.`
                                    : undefined;
                        const changesCoverage = Boolean(coverageInstruction);
                        const wireAction = changesCoverage || (Boolean(preview) && request.action === 'answer')
                            ? 'generate-now'
                            : request.action;
                        const suggestionDecisions = buildOnboardingSuggestionDecisions(workspace.SuccessProfile.Proposals);
                        const messages = coverageInstruction
                            ? [...request.messages, { role: 'user' as const, content: coverageInstruction }]
                            : request.messages;

                        setAiBusy(true); setError('');
                        try {
                            const aiClient = new AiTemplateClient();
                            let response = await aiClient.ContinueContextualChat(buildContextualRequest('onboarding-success-profile', wireAction, {
                                PositionId: workspace.PositionDraft.PositionId,
                                PositionTitle: workspace.PositionDraft.Title ?? '',
                                JobDescription: workspace.PositionDraft.Description ?? '',
                                Answers: nextAnswers,
                                Messages: messages.map(({ role, content }) => ({ role, content })),
                                ContextSummary: request.contextSummary,
                                GenerateNow: request.action === 'generate-now',
                                InteractionId: workspace.SuccessProfile.InteractionId || preview?.InteractionId || undefined,
                                TemplateName: templateName,
                                ProcessType: ProcessType.Evaluate,
                                ExistingPreview: preview ?? null,
                                Regenerate: request.action === 'coverage-regenerate',
                                SuggestionDecisions: suggestionDecisions,
                                CoverageConfirmations: nextConfirmations,
                                CoverageContractVersion: requestVersion,
                                ChangedCoverageGroups: changedCoverageGroups,
                            }));
                            if (response.State === 'ReadyToGenerate' && !request.action.startsWith('coverage-')) {
                                response = await aiClient.ContinueContextualChat(buildContextualRequest('onboarding-success-profile', 'generate-now', {
                                    PositionId: workspace.PositionDraft.PositionId,
                                    PositionTitle: workspace.PositionDraft.Title ?? '',
                                    JobDescription: workspace.PositionDraft.Description ?? '',
                                    Answers: nextAnswers,
                                    Messages: messages.map(({ role, content }) => ({ role, content })),
                                    ContextSummary: response.ContextSummary,
                                    GenerateNow: true,
                                    InteractionId: response.InteractionId,
                                    TemplateName: templateName,
                                    ProcessType: ProcessType.Evaluate,
                                    ExistingPreview: preview ?? null,
                                    Regenerate: Boolean(preview),
                                    SuggestionDecisions: suggestionDecisions,
                                }));
                            }
                            setCurrentGap(response.GapKey);
                            if (confirmation) {
                                setCoverageConfirmations(nextConfirmations);
                            }

                            if (!response.TemplatePreview) {
                                if (response.CoverageDecision)
                                    setCoverageContractVersion(response.CoverageDecision.CoverageContractVersion);
                                const serverConfirmedCurrentCoverage = response.State === 'ReadyToGenerate' && !response.CoverageDecision;
                                setCoverageReady(serverConfirmedCurrentCoverage);
                                if (serverConfirmedCurrentCoverage)
                                    setChangedCoverageGroups([]);
                                const mapped = mapContextualResponse(response) as ContextualAiTurnResult<AiTemplatePreview | null>;
                                return { ...mapped, progressLabel: response.State === 'AskQuestion' ? 'Learning about the role' : 'Reviewing success-profile coverage' };
                            }

                            const freshPreview = applyOnboardingDenials(response.TemplatePreview, suggestionDecisions);
                            const updated = await client.SaveManualSuccessProfile({
                                ExpectedVersion: workspace.Version,
                                IdempotencyKey: generationKey.current,
                                Preview: freshPreview,
                            });
                            generationKey.current = createKey();
                            setWorkspace(updated);
                            track('ai_generated', 'SuccessProfile');

                            const retainedConfirmations = changesCoverage && coverageCategory
                                ? nextConfirmations.filter(item => item.ProficiencyGroup !== coverageCategory)
                                : nextConfirmations;
                            const changedGroups = changesCoverage && coverageCategory
                                ? changedCoverageGroups.includes(coverageCategory)
                                    ? changedCoverageGroups
                                    : [...changedCoverageGroups, coverageCategory]
                                : changedCoverageGroups;
                            if (changesCoverage) {
                                setCoverageConfirmations(retainedConfirmations);
                                setChangedCoverageGroups(changedGroups);
                            }
                            const coverageResponse = await aiClient.ContinueContextualChat(buildContextualRequest('onboarding-success-profile', 'coverage-regenerate', {
                                PositionId: workspace.PositionDraft.PositionId,
                                PositionTitle: workspace.PositionDraft.Title ?? '',
                                JobDescription: workspace.PositionDraft.Description ?? '',
                                Answers: nextAnswers,
                                Messages: messages.map(({ role, content }) => ({ role, content })),
                                ContextSummary: response.ContextSummary,
                                GenerateNow: false,
                                InteractionId: response.InteractionId,
                                TemplateName: templateName,
                                ProcessType: ProcessType.Evaluate,
                                ExistingPreview: freshPreview,
                                Regenerate: false,
                                SuggestionDecisions: [],
                                CoverageConfirmations: retainedConfirmations,
                                CoverageContractVersion: response.CoverageDecision?.CoverageContractVersion ?? requestVersion,
                                ChangedCoverageGroups: changedGroups,
                            }));
                            if (coverageResponse.CoverageDecision)
                                setCoverageContractVersion(coverageResponse.CoverageDecision.CoverageContractVersion);
                            const serverConfirmedCurrentCoverage = coverageResponse.State === 'ReadyToGenerate' && !coverageResponse.CoverageDecision;
                            setCoverageReady(serverConfirmedCurrentCoverage);
                            if (serverConfirmedCurrentCoverage)
                                setChangedCoverageGroups([]);
                            const mapped = mapContextualResponse(coverageResponse) as ContextualAiTurnResult<AiTemplatePreview | null>;
                            return {
                                ...mapped,
                                result: freshPreview,
                                assistantMessage: freshPreview.AssistantMessage || mapped.assistantMessage,
                                progressLabel: coverageResponse.CoverageDecision ? 'Reviewing success-profile coverage' : 'Success profile ready for review',
                            };
                        } catch {
                            setError('The success profile assistant is unavailable. Your saved setup is safe; retry or use the manual option.');
                            throw new Error('Success profile assistant did not respond');
                        } finally {
                            setAiBusy(false);
                        }
                    },
                } as ContextualAiWorkflowAdapter<AiTemplatePreview | null>}
                testId="onboarding-success-profile-ai-workspace"
                transcriptTestId="onboarding-template-chat-scroll"
                composerTestId="onboarding-template-ai-composer"
                composerLabel={preview ? 'Ask AI to revise the profile' : 'Answer in your own words'}
                onMessagesChange={next => setAiMessages(next)}
                onContextSummaryChange={summary => rememberContextSummary(`position:${workspace.PositionDraft.PositionId ?? workspace.Id}`, summary)}
            />
        </ContextualAiDialog>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} justifyContent="flex-end">{!templateResource && proposals.length > 0 && <Button variant="contained" disabled={busy || !coverageReady || changedCoverageGroups.length > 0 || proposals.every(item => item.Decision === 'Rejected')} onClick={() => void createTemplate()}>Save included profile</Button>}{templateResource && <Button variant="contained" onClick={onNext}>Continue</Button>}</Stack>
    </Stack>;
};

const EmployeesStep = ({ workspace, setWorkspace, client, busy, setBusy, setError, onNext, onSkip }) => {
    const blank: TalentDraftInput = { FirstName: '', LastName: '', Email: '', PositionId: workspace.PositionDraft.PositionId!, SupervisorEmail: '' };
    const [draft, setDraft] = useState(blank);
    const [paste, setPaste] = useState('');
    const [successMessage, setSuccessMessage] = useState('');
    const discardOperations = useRef(new Map<string, { idempotencyKey: string; expectedVersion: string; inFlight: boolean }>());
    const saveDrafts = async (drafts: TalentDraftInput[]) => {
        setBusy(true); setError('');
        try {
            let updated = workspace;
            for (const employee of drafts) {
                updated = await client.SaveTalentDraft({
                    ExpectedVersion: updated.Version,
                    IdempotencyKey: createKey(),
                    DraftId: employee.DraftId,
                    FirstName: employee.FirstName,
                    LastName: employee.LastName,
                    Email: employee.Email,
                    PositionId: workspace.PositionDraft.PositionId!,
                    SupervisorEmail: employee.SupervisorEmail,
                });
            }
            setWorkspace(updated); setDraft(blank); setPaste(''); track('employee_draft_saved', 'Employees');
        } catch { setError('One or more Talent drafts could not be saved. Refresh to see the authoritative roster before retrying.'); }
        finally { setBusy(false); }
    };
    const discardDraft = async (candidate: OnboardingTalentDraft) => {
        let operation = discardOperations.current.get(candidate.Id);
        if (operation?.inFlight)
            return;
        if (!operation) {
            operation = { idempotencyKey: createKey(), expectedVersion: workspace.Version, inFlight: false };
            discardOperations.current.set(candidate.Id, operation);
        }
        // Set this before React processes setBusy so two clicks in the same event
        // turn cannot dispatch two logical removals.
        operation.inFlight = true;
        setBusy(true); setError('');
        try {
            const updated = await client.DiscardTalentDraft(candidate.Id, {
                ExpectedVersion: operation.expectedVersion,
                IdempotencyKey: operation.idempotencyKey,
            });
            setWorkspace(updated);
            discardOperations.current.delete(candidate.Id);
            setSuccessMessage(`${candidate.FirstName} ${candidate.LastName} was removed from the Talent activation roster.`);
            track('employee_draft_discarded', 'Employees');
        } catch { setError('That Talent draft could not be removed. Refresh the roster and retry.'); }
        finally {
            const pending = discardOperations.current.get(candidate.Id);
            if (pending)
                pending.inFlight = false;
            setBusy(false);
        }
    };
    const addPaste = () => void saveDrafts(paste.split(/\r?\n/).map(parseEmployeeLine).filter(Boolean) as TalentDraftInput[]);
    return <Stack spacing={2.5}>
        <Alert severity="info" icon={<PersonAddAltRounded />}><strong>Talent records represent the teammates whose performance and development you manage.</strong> A teammate may not have system access. Unlike setup collaborators, active Talent affect the pricing estimate. These remain resumable drafts until activation.</Alert>
        <Paper variant="outlined" sx={{ p: 2 }}><Typography fontWeight={700} sx={{ mb: 1.5 }}>{draft.DraftId ? 'Edit Talent draft' : 'Add a teammate'}</Typography><Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.25}><TextField label="First name" value={draft.FirstName} onChange={e => setDraft({ ...draft, FirstName: e.target.value })} fullWidth /><TextField label="Last name" value={draft.LastName} onChange={e => setDraft({ ...draft, LastName: e.target.value })} fullWidth /><TextField required label="Work email" type="email" value={draft.Email} onChange={e => setDraft({ ...draft, Email: e.target.value })} fullWidth /><Button startIcon={<AddRounded />} variant="outlined" disabled={busy || !draft.FirstName.trim() || !draft.LastName.trim() || !/^\S+@\S+\.\S+$/.test(draft.Email)} onClick={() => void saveDrafts([draft])} sx={{ minHeight: 56, flexShrink: 0 }}>{draft.DraftId ? 'Save changes' : 'Save Talent draft'}</Button></Stack></Paper>
        <Paper variant="outlined" sx={{ p: 2 }}><Typography fontWeight={700}>Paste a list</Typography><Typography variant="caption" color="text.secondary">One teammate per line: First name Last name, email</Typography><TextField multiline minRows={3} fullWidth value={paste} onChange={e => setPaste(e.target.value)} sx={{ my: 1.5 }} /><Button variant="outlined" disabled={!paste.trim()} onClick={addPaste}>Add list as drafts</Button></Paper>
        <Button variant="outlined" disabled sx={{ alignSelf: 'flex-start' }}>Import from HR system — coming soon</Button>
        {workspace.TalentDrafts.length > 0 && <Stack spacing={1} aria-label="Talent onboarding roster">{workspace.TalentDrafts.map(item => <Paper key={item.Id} variant="outlined" sx={{ p: 1.25 }}><Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} justifyContent="space-between"><Box sx={{ minWidth: 0 }}><Typography fontWeight={650}>{item.FirstName} {item.LastName}</Typography><Typography variant="caption" color="text.secondary" sx={{ overflowWrap: 'anywhere' }}>Teammate being developed · {item.Email}</Typography></Box><Stack direction="row" spacing={1} alignItems="center" sx={{ alignSelf: { xs: 'stretch', sm: 'center' } }}><Chip size="small" label={item.MaterializedTalentId ? 'Active' : 'Draft'} color={item.MaterializedTalentId ? 'success' : 'default'} />{!item.MaterializedTalentId && <><Button disabled={busy} onClick={() => setDraft({ DraftId: item.Id, FirstName: item.FirstName, LastName: item.LastName, Email: item.Email, PositionId: item.PositionId, SupervisorEmail: item.SupervisorEmail ?? '' })}>Edit</Button><Button disabled={busy} color="error" aria-label={`Remove ${item.FirstName} ${item.LastName} from activation roster`} onClick={() => void discardDraft(item)}>Remove</Button></>}</Stack></Stack></Paper>)}</Stack>}
        <Snackbar open={Boolean(successMessage)} autoHideDuration={5000} onClose={() => setSuccessMessage('')} message={successMessage} />
        <Stack direction={{ xs: 'column-reverse', sm: 'row' }} spacing={1} justifyContent="flex-end"><Button disabled={busy} onClick={onSkip}>Skip for now</Button><Button variant="contained" disabled={busy} onClick={onNext}>Continue</Button></Stack>
    </Stack>;
};

const ActivationStep = ({ workspace, setWorkspace, client, busy, setBusy, setError, subscriptionRequired, hasSubscriptionAccess }) => {
    const [confirmed, setConfirmed] = useState(false);
    const [billing, setBilling] = useState<BillingSummary | null>(null);
    const [billingBusy, setBillingBusy] = useState(false);
    const activationKey = useRef(createKey()).current;
    const activation = workspace.Activation;
    const refreshBilling = async () => {
        if (!subscriptionRequired)
            return;
        setBillingBusy(true);
        try { setBilling(await new ManageOrganizationClient().GetBillingSummary()); }
        catch { setError('We could not refresh the checkout status. Wait a moment and try again.'); }
        finally { setBillingBusy(false); }
    };
    useEffect(() => {
        let active = true;
        client.GetActivationQuote()
            .then(quote => active && setWorkspace(current => ({ ...current!, Activation: quote })))
            .catch(() => active && setError('The current pricing estimate could not be loaded. Refresh before activating.'));
        if (subscriptionRequired)
            new ManageOrganizationClient().GetBillingSummary().then(summary => active && setBilling(summary)).catch(() => undefined);
        return () => { active = false; };
    }, []); // eslint-disable-line react-hooks/exhaustive-deps
    const billingActive = !subscriptionRequired || hasSubscriptionAccess || billing?.IsActive;
    const checkoutPending = subscriptionRequired && billing?.HasSubscription && !billing.IsActive
        && !['Canceled', 'Cancelled', 'None', 'IncompleteExpired'].includes(billing.Status);
    const canStartCheckout = subscriptionRequired && !billingActive && !checkoutPending;
    const activate = async () => {
        setBusy(true); setError('');
        try {
            if (canStartCheckout) {
                const checkout = await new ManageOrganizationClient().CreateBillingCheckout();
                track('checkout_started', 'Activation');
                sessionStorage.setItem('synergist:onboardingBillingReturn', 'true');
                window.location.assign(checkout.Url);
                return;
            }
            if (!billingActive) {
                setError('Your payment is still processing. Refresh the billing status before activating.');
                return;
            }
            const updated = await client.ConfirmActivation({ ExpectedVersion: workspace.Version, IdempotencyKey: activationKey });
            setWorkspace(updated);
            track('activation_confirmed', 'Activation');
        } catch { setError('Activation was not completed. No duplicate charge was created; review the message and try again.'); }
        finally { setBusy(false); }
    };
    if (!activation)
        return <Box role="status" sx={{ display: 'grid', placeItems: 'center', minHeight: 240 }}><CircularProgress /></Box>;
    return <Stack spacing={2.5}>
        <Paper variant="outlined" sx={{ p: { xs: 2, sm: 3 } }}><Stack spacing={2}><Stack direction="row" justifyContent="space-between"><Typography>Active Talent after activation</Typography><Typography fontWeight={800}>{activation.ActiveEmployeeCount}</Typography></Stack><Stack direction="row" justifyContent="space-between"><Typography>Price per active Talent</Typography><Typography fontWeight={800}>{money(activation.UnitAmountCents / 100, activation.Currency)} / month</Typography></Stack><Stack direction="row" justifyContent="space-between"><Typography>Minimum billed Talent</Typography><Typography fontWeight={800}>{activation.MinimumEmployeeCount}</Typography></Stack><Divider /><Stack direction="row" justifyContent="space-between"><Typography variant="h6">Estimated monthly total</Typography><Typography variant="h6" fontWeight={900}>{money(activation.EstimatedMonthlyTotalCents / 100, activation.Currency)}</Typography></Stack></Stack></Paper>
        {billingActive ? <Alert severity="success">Your organization already has subscription access. No checkout is required.</Alert> : checkoutPending ? <Alert severity="info" action={<Button disabled={billingBusy} onClick={() => void refreshBilling()}>Refresh status</Button>}>Payment status: {billing?.Status || 'processing'}. Activation will become available after Stripe confirms it.</Alert> : <Alert severity="info">You pay only for active Talent, subject to a minimum billing quantity of {activation.MinimumEmployeeCount}. Checkout shows the final amount before payment.</Alert>}
        <FormControlLabel control={<Checkbox checked={confirmed} onChange={e => setConfirmed(e.target.checked)} />} label="I reviewed the active Talent count and estimated monthly cost." />
        <Button variant="contained" disabled={busy || billingBusy || !confirmed || Boolean(checkoutPending)} onClick={() => void activate()} sx={{ minHeight: 48, alignSelf: { xs: 'stretch', sm: 'flex-end' } }}>{billingActive ? 'Confirm access' : 'Continue to secure checkout'}</Button>
    </Stack>;
};

const SummaryStep = ({ workspace, client, busy, setBusy, setError, onComplete }) => {
    const items = [
        ['Company context', workspace.Checklist.CompanyProfile],
        ['Setup collaborators', workspace.Collaborators.length > 0],
        ['First position and job description', workspace.Checklist.Position],
        ['Success profile', workspace.Checklist.SuccessProfile],
        ['Talent', workspace.TalentDrafts.some(item => Boolean(item.MaterializedTalentId))],
        ['Access and pricing', workspace.Checklist.Activation],
    ];
    const complete = async () => {
        setBusy(true); setError('');
        try { await client.Complete({ ExpectedVersion: workspace.Version, IdempotencyKey: createKey() }); onComplete(); }
        catch { setError('Setup could not be finalized. Your progress is saved; please try again.'); }
        finally { setBusy(false); }
    };
    return <Stack spacing={2.5} alignItems="stretch"><Box sx={{ textAlign: 'center' }}><TaskAltRounded color="success" sx={{ fontSize: 64 }} /><Typography variant="h5" fontWeight={800}>Your foundation is ready</Typography><Typography color="text.secondary">Here’s what Synergist Pro will carry forward.</Typography></Box><Stack spacing={1}>{items.map(([label, done]) => <Paper key={label as string} variant="outlined" sx={{ p: 1.5 }}><Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.25} alignItems={{ sm: 'center' }}><CheckCircleRounded color={done ? 'success' : 'disabled'} /><Typography sx={{ flex: 1 }}>{label as string}</Typography><Chip size="small" label={done ? 'Ready' : 'Optional — add later'} sx={{ alignSelf: 'flex-start' }} /></Stack></Paper>)}</Stack><Alert severity="info">After setup is complete, you can add more setup collaborators, positions, templates, and Talent records from their normal management pages.</Alert><Button variant="contained" size="large" disabled={busy} onClick={() => void complete()} sx={{ minHeight: 52, alignSelf: { xs: 'stretch', sm: 'center' }, px: 4 }}>Go to your dashboard</Button></Stack>;
};

const stepDescription = (step: OnboardingStep) => ({
    CompanyProfile: 'Give the AI reliable context about who you are and what matters to your organization.',
    Collaborators: 'Invite administrators or supervisors who will help configure Synergist. This step is optional.',
    FirstPosition: 'Start with one real role. AI helps draft it, while you remain the author and approver.',
    SuccessProfile: 'Choose the abilities and behaviors that should guide consistent, role-based coaching.',
    Employees: 'Add teammates only when you are ready. They remain Talent drafts and do not count toward billing.',
    Activation: 'See the server-calculated estimate before confirming anything.',
    Complete: 'Review what was created and move into your first real development workflow.',
}[step]);

const requiredSuccessGroups = ['Sustainable', 'Performant', 'Adaptable', 'Communication', 'Culture'];

const onboardingDiscoveryTopicGap: Record<TemplateDiscoveryTopic, AiContextualAiAnswer['GapKey']> = {
    outcomes: 'RoleOutcomesAndFailureCosts',
    abilities: 'TechnicalDomainAbilities',
    autonomy: 'DecisionAuthority',
    'risks-and-prevention': 'HighImpactMistakes',
    'positive-practices': 'PositiveBehaviors',
    collaboration: 'CollaborationAudiences',
    'safety-compliance-ethics': 'SafetyComplianceEthics',
    'performance-contrast': 'PerformanceDistinction',
};

const buildOnboardingSuggestionDecisions = (
    proposals: OnboardingWorkspace['SuccessProfile']['Proposals'],
): AiTemplateSuggestionDecision[] => proposals
    .filter(proposal => proposal.Decision === 'Rejected')
    .map(proposal => ({
        ProficiencyGroup: proposal.Group,
        Description: proposal.Description,
        Type: proposal.Type as AiTemplateSuggestionDecision['Type'],
        IsNegative: proposal.IsNegative,
        Decision: 'denied',
    }));

const applyOnboardingDenials = (
    preview: AiTemplatePreview,
    decisions: AiTemplateSuggestionDecision[],
): AiTemplatePreview => {
    const denied = new Set(decisions
        .filter(decision => decision.Decision === 'denied')
        .map(decision => [
            decision.ProficiencyGroup,
            decision.Type,
            decision.IsNegative ? 'negative' : 'positive',
            decision.Description.trim().toLowerCase(),
        ].join('|')));
    return {
        ...preview,
        ProficiencyGroups: preview.ProficiencyGroups.map(group => ({
            ...group,
            Proficiencies: group.Proficiencies.filter(item => !denied.has([
                group.ProficiencyGroup,
                item.Type,
                item.IsNegative ? 'negative' : 'positive',
                item.Description.trim().toLowerCase(),
            ].join('|'))),
        })),
    };
};

const parseManualSuccessItems = (value: string) => value.split(/\r?\n/).map(line => line.trim()).filter(Boolean).map(line => {
    const ability = /^ability\s*:/i.test(line);
    const negative = /^behavior\s*-\s*:/i.test(line);
    const positive = /^behavior\s*\+\s*:/i.test(line);
    if (!ability && !negative && !positive)
        return null;
    return {
        Description: line.replace(/^(ability|behavior\s*[+-])\s*:/i, '').trim(),
        Type: ability ? 'Ability' : 'Behavior',
        Weight: negative ? 10 : 5,
        IsNegative: negative,
        ReusesExistingProficiency: false,
    };
}).filter(item => Boolean(item?.Description)) as Array<{ Description: string; Type: string; Weight: number; IsNegative: boolean; ReusesExistingProficiency: boolean }>;

const manualGroupIsValid = (value: string) => {
    const items = parseManualSuccessItems(value);
    return items.length > 0
        && new Set(items.map(item => `${item.Type}|${item.IsNegative}|${item.Description.toLowerCase()}`)).size === items.length;
};

const manualProfileValidationIssue = (groups: Record<string, string>) => {
    if (!requiredSuccessGroups.every(group => manualGroupIsValid(groups[group])))
        return 'Add at least one distinct role criterion to every category, then decide whether its coverage is sufficient.';
    const items = requiredSuccessGroups.flatMap(group => parseManualSuccessItems(groups[group]));
    const negativeCount = items.filter(item => item.IsNegative).length;
    if (!items.some(item => item.Type === 'Ability' && !item.IsNegative))
        return 'Include at least one ability somewhere in the success profile.';
    if (!items.some(item => item.Type === 'Behavior' && !item.IsNegative))
        return 'Include at least one positive behavior somewhere in the success profile.';
    if (negativeCount > Math.floor(items.length * 0.2))
        return 'Counterproductive behaviors must remain sparse—no more than one in five items across the whole profile.';
    return '';
};

const parseEmployeeLine = (line: string): TalentDraftInput | null => {
    const [namePart, email = ''] = line.split(',').map(part => part.trim());
    const names = namePart?.split(/\s+/).filter(Boolean) ?? [];
    if (names.length < 2)
        return null;
    if (!/^\S+@\S+\.\S+$/.test(email))
        return null;
    return { FirstName: names[0], LastName: names.slice(1).join(' '), Email: email, PositionId: '', SupervisorEmail: '' };
};

export default OnboardingPage;
